import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/shared/stores/authStore';
import { fetchCurrentUser } from '@/shared/api/authApi';
import { PageLoader } from '@/shared/ui/loading/Loading';

export function AuthInitializer({ children }: { children: React.ReactNode }) {
  const [isReady, setIsReady] = useState(false);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    async function init() {
      const store = useAuthStore.getState();
      const { token, user } = store;

      if (!token) {
        store.setLoading(false);
        setIsReady(true);
        return;
      }

      // Si ya tenemos usuario persistido, mostramos la app de inmediato
      // y refrescamos en segundo plano para recoger cambios de
      // permisos/roles sin bloquear la UI ni requerir cerrar sesión.
      if (user) {
        store.setLoading(false);
        setIsReady(true);
        fetchCurrentUser()
          .then(refreshedUser => useAuthStore.getState().setUser(refreshedUser))
          .catch(() => {
            // Ignoramos errores transitorios (sin red, timeout): la app sigue
            // con el usuario cacheado. El interceptor de 401 maneja tokens
            // realmente inválidos.
          });
        return;
      }

      try {
        const refreshedUser = await fetchCurrentUser();
        store.setUser(refreshedUser);
      } catch {
        // No hacemos logout aquí para no borrar la sesión en errores transitorios
        // durante desarrollo. El interceptor de 401 maneja tokens realmente inválidos.
        store.setLoading(false);
      } finally {
        setIsReady(true);
      }
    }

    init();
  }, []);

  if (!isReady) {
    return <PageLoader message="Inicializando aplicación..." className="min-h-screen" />;
  }

  return <>{children}</>;
}
